import * as React from "react";
import { Calendar as CalendarIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import ReactDatePicker from "react-datepicker";
import { ptBR, enUS } from "date-fns/locale";
import { cn } from "@/infrastructure/lib/utils";
import "./date-picker.css";
import { useTranslation } from "react-i18next";

export interface DatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  excludeDates?: Date[];
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  showTimeSelect?: boolean;
  timeFormat?: string;
  timeIntervals?: number;
  portalId?: string;
  width?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  className,
  clearable = true,
  disabled = false,
  dateFormat,
  minDate,
  maxDate,
  excludeDates,
  showMonthDropdown = true,
  showYearDropdown = true,
  showTimeSelect = false,
  timeFormat = "HH:mm",
  timeIntervals = 30,
  portalId,
  width = "100%",
}: DatePickerProps) {
  const { i18n } = useTranslation();
  
  const locale = i18n.language === 'en' ? enUS : ptBR;
  
  const defaultPlaceholder = i18n.language === 'en' ? "Select a date" : "Selecione uma data";
  
  const defaultDateFormat = i18n.language === 'en' ? "MM/dd/yyyy" : "dd/MM/yyyy";
  
  const finalPlaceholder = placeholder || defaultPlaceholder;
  const finalDateFormat = dateFormat || defaultDateFormat;
  const handleChange = (date: Date | null) => {
    onChange(date || undefined);
  };

  // Componente customizado para o botão de input
  const CustomInput = React.forwardRef<HTMLButtonElement, { value?: string; onClick?: () => void }>(
    ({ value, onClick }, ref) => (
      <button
        type="button"
        onClick={onClick}
        ref={ref}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          !value && "text-muted-foreground",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        disabled={disabled}
      >
        <div className="flex items-center min-w-0 flex-1">
          <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className="truncate">{value || finalPlaceholder}</span>
        </div>
        {clearable && value && !disabled && (
          <X 
            className="ml-2 h-4 w-4 hover:text-destructive transition-colors flex-shrink-0" 
            onClick={(e) => {
              e.stopPropagation();
              handleChange(null);
            }}
          />
        )}
      </button>
    )
  );
  
  CustomInput.displayName = "DatePickerCustomInput";

  return (
    <div className="rdp-datepicker-wrapper" style={{ width }}>
      <ReactDatePicker
        selected={value}
        onChange={handleChange}
        dateFormat={finalDateFormat}
        locale={locale}
        minDate={minDate}
        maxDate={maxDate}
        excludeDates={excludeDates}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        timeIntervals={timeIntervals}
        isClearable={false}
        disabled={disabled}
        customInput={<CustomInput />}
        portalId={portalId}
        inline={false}
        calendarStartDay={i18n.language === 'en' ? 0 : 1} // 0 for Sunday (US), 1 for Monday (BR)
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 1)} // Abbreviate day name
        popperPlacement="bottom-start"
        monthsShown={1}
        showPopperArrow={false}
        renderCustomHeader={({ 
          date, 
          changeYear, 
          changeMonth, 
          decreaseMonth, 
          increaseMonth, 
          prevMonthButtonDisabled, 
          nextMonthButtonDisabled 
        }) => (
          <div className="react-datepicker__custom-header">
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              className="react-datepicker__navigation-button prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="react-datepicker__header-controls">
              <select
                value={date.getFullYear()}
                onChange={({ target: { value } }) => changeYear(parseInt(value))}
                className="react-datepicker__year-select"
              >
                {Array.from({ length: 20 }, (_, i) => date.getFullYear() - 10 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <select
                value={date.getMonth()}
                onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                className="react-datepicker__month-select"
              >
                {i18n.language === 'en' ? [
                  "January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"
                ].map((month, i) => (
                  <option key={i} value={i}>{month}</option>
                )) : [
                  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
                ].map((month, i) => (
                  <option key={i} value={i}>{month}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              className="react-datepicker__navigation-button next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      />
    </div>
  );
}
